import React, { Fragment } from "react";
import { getFieldPath } from "utils/form";
import get from "lodash/get";
import cloneDeep from "lodash/cloneDeep";
import { useFormikContext } from "formik";
import DebounceTextarea from "components/DebounceTextarea";
import classnames from "classnames";
import { STATE_KEYS } from "utils/constants/state";
import { IDENTITY_VERIFICATION_TYPE } from "utils/constants/identity";
import Toggle from "react-toggle";
import { getToggleProps } from "utils/form";
import { useAppCheckout } from "utils/selectors";
import OptionListPicker from "components/OptionListPicker";
import { getUUID } from "utils/uuid";
import { LIST_PICKER_CUSTOM_CLASSES } from "utils/constants/ui";
import { useIsManifestEditMode } from "utils/hooks/manifest";

const ValuesFields = ({ name }) => {
  const {
    values,
    touched,
    setFieldValue,
    setFieldTouched
  } = useFormikContext();
  const isEditMode = useIsManifestEditMode();

  const headerPath = getFieldPath(name, STATE_KEYS.IDENTITY.HEADER);
  const headerValue = get(values, headerPath);

  const titlePath = getFieldPath(name, STATE_KEYS.IDENTITY.TITLE, "");
  const titleValue = get(values, titlePath);

  const promptPath = getFieldPath(name, STATE_KEYS.IDENTITY.PROMPT, "");
  const promptValue = get(values, promptPath);
  const checkout = useAppCheckout();
  const identityTypeOptions =
    get(checkout, STATE_KEYS.APP_CHECKOUT.IDENTITY_TYPES) || [];

  const typePath = getFieldPath(name, STATE_KEYS.IDENTITY.TYPE);
  const typeValue = get(values, typePath);
  const isDocument = typeValue === IDENTITY_VERIFICATION_TYPE.DOCUMENT;

  const verificationTypeProps = {
    label: "Pricing",
    value: typeValue,
    name: typePath,
    options: identityTypeOptions,
    canUnset: false,
    disabled: isEditMode,
    customClasses: LIST_PICKER_CUSTOM_CLASSES
  };

  let optionsFields = null;
  if (isDocument) {
    const identityDocumentAllowedTypeOptions =
      get(checkout, STATE_KEYS.APP_CHECKOUT.IDENTITY_DOCUMENT_ALLOWED_TYPES) ||
      [];
    const allowedTypesPath = getFieldPath(
      name,
      STATE_KEYS.IDENTITY.ALLOWED_TYPES
    );
    const allowedTypesValue = get(values, allowedTypesPath) || [];
    const selectedTypes = allowedTypesValue.map(({ uid }) => uid);
    const allowedTypeProps = {
      label: "Allowed types",
      value: selectedTypes,
      name: allowedTypesPath,
      multiselect: true,
      options: identityDocumentAllowedTypeOptions,
      disabled: isEditMode,
      onClick: (_, selectedValue) => {
        const updatedTypes = cloneDeep(allowedTypesValue);
        const matchIndex =
          Array.isArray(allowedTypesValue) &&
          allowedTypesValue.findIndex(({ uid }) => uid === selectedValue);

        let shouldUpdate = false;
        if (matchIndex >= 0) {
          shouldUpdate = true;
          updatedTypes.splice(matchIndex, 1);
        } else {
          shouldUpdate = true;
          updatedTypes.push({
            uid: selectedValue,
            uuid: getUUID()
          });
        }

        if (shouldUpdate) {
          setFieldValue(allowedTypesPath, updatedTypes);
          if (!get(touched, allowedTypesPath)) {
            setFieldTouched(name, true);
          }
        }
      },
      canUnset: true,
      customClasses: LIST_PICKER_CUSTOM_CLASSES
    };

    const requireIdNumberPath = getFieldPath(
      name,
      STATE_KEYS.IDENTITY.REQUIRE_ID_NUMBER
    );
    const requireIdNumberValue = get(values, requireIdNumberPath);

    const requireLiveCapturePath = getFieldPath(
      name,
      STATE_KEYS.IDENTITY.REQUIRE_LIVE_CAPTURE
    );
    const requireLiveCaptureValue = get(values, requireLiveCapturePath);

    const requireMatchingSelfiePath = getFieldPath(
      name,
      STATE_KEYS.IDENTITY.REQUIRE_MATCHING_SELFIE
    );
    const requireMatchingSelfieValue = get(values, requireMatchingSelfiePath);

    optionsFields = (
      <div className="flex flex-column pt3">
        <div className="w-100">
          <div className="f5 pb2 fw6">Allowed documents</div>
          <div className="f6 lh-copy">
            Set which government issued document types are allowed for
            verification.
          </div>
          <div className="f7 lh-copy pt1">
            When none are selected then all will be allowed.
          </div>
          <div className="pt3">
            <OptionListPicker {...allowedTypeProps} />
          </div>
        </div>
        <div className="pt3">
          <div className="f5 pb2 fw6">Additional requirements</div>
          <div className="f6 lh-copy">
            Optional requirements to help ensure the authenticity of the
            document.
          </div>
          <div className="pt3 w-100">
            <div className="flex flex-row w-100 justify-between items-center pb2">
              <div className="f6 dib fw6">ID Number</div>
              <Toggle
                {...getToggleProps({
                  disabled: isEditMode,
                  active: requireIdNumberValue,
                  setFieldValue: (_, active) => {
                    setFieldValue(requireIdNumberPath, active);
                    setFieldTouched(requireIdNumberPath, true);
                  }
                })}
              />
            </div>
            <div className="f6 lh-copy">
              Collect an ID number and perform an ID number check with the
              document’s extracted name and date of birth.
            </div>
          </div>
        </div>
        <div className="pt3 mt2">
          <div className="flex flex-row w-100 justify-between items-center pb2">
            <div className="f6 dib fw6">Live Capture</div>
            <Toggle
              {...getToggleProps({
                disabled: isEditMode,
                active: requireLiveCaptureValue,
                setFieldValue: (_, active) => {
                  setFieldValue(requireLiveCapturePath, active);
                  setFieldTouched(requireLiveCapturePath, true);
                }
              })}
            />
          </div>
          <div className="f6 lh-copy">
            Disable image uploads, identity document images have to be captured
            using the device’s camera.
          </div>
        </div>
        <div className="pt3 mt2">
          <div className="flex flex-row w-100 justify-between items-center pb2">
            <div className="f6 dib fw6">Matching selfie</div>
            <Toggle
              {...getToggleProps({
                disabled: isEditMode,
                active: requireMatchingSelfieValue,
                setFieldValue: (_, active) => {
                  setFieldValue(requireMatchingSelfiePath, active);
                  setFieldTouched(requireMatchingSelfiePath, true);
                }
              })}
            />
          </div>
          <div className="f6 lh-copy">
            Capture a face image and perform a selfie check comparing a photo ID
            and a picture of your user’s face.
          </div>
        </div>
      </div>
    );
  }

  return (
    <Fragment>
      <div className="f5 pb2 fw6">Presentation</div>
      <div className="f6 lh-copy">
        Control the labels for your identity field.
      </div>
      <div className="w-100 pt3">
        {[
          {
            label: "Header",
            name: headerPath,
            uid: "header",
            placeholder: "Set a header",
            value: headerValue
          },
          {
            label: "Title",
            name: titlePath,
            uid: "title",
            placeholder: "Set a title",
            value: titleValue
          },
          {
            label: "Prompt",
            uid: "prompt",
            name: promptPath,
            placeholder: "Set a prompt",
            value: promptValue
          }
        ].map((field, fieldIx) => (
          <div
            key={field.uid}
            className={classnames("w-100", {
              pt3: fieldIx
            })}
          >
            <label className="f6 mb2 dib fw6">{field.label}</label>
            <DebounceTextarea
              inputValue={field.value}
              debounce={500}
              disabled={isEditMode}
              minRows={1}
              name={field.name}
              valueSet={(val) => {
                setFieldValue(field.name, val);
                if (!get(touched, field.name)) {
                  setFieldTouched(field.name, true);
                }
              }}
            />
          </div>
        ))}
      </div>
      <div className="w-100 pt3">
        <div className="f5 pb2 fw6">Type</div>
        <div className="f6 lh-copy">
          The type of verification check to be performed.
        </div>
        <div className="pt3">
          <OptionListPicker {...verificationTypeProps} />
        </div>
      </div>
      {optionsFields}
    </Fragment>
  );
};

export default ValuesFields;
